'use client';

import {
  useState,
  useTransition,
  useEffect,
  useMemo,
  useCallback,
} from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useRouter, useSearchParams } from 'next/navigation';
import { useTranslations } from '@/hooks/useTranslations';
import * as z from 'zod';
import { AccountSettingsSchema } from '@/schemas';
import { accountSettings } from '@/actions';
import { ExtendedUser } from '@/types/next-auth';
import { useSession } from 'next-auth/react';

interface UseMultiStepFormProps {
  user: ExtendedUser;
  totalSteps?: number;
  redirectOnSuccess?: string;
}

export const useMultiStepForm = ({
  user,
  totalSteps = 5,
  redirectOnSuccess = '/dashboard',
}: UseMultiStepFormProps) => {
  const t = useTranslations('UserSettings');
  const common = useTranslations('Common');
  const router = useRouter();
  const searchParams = useSearchParams();

  const [error, setError] = useState<string>();
  const [success, setSuccess] = useState<string>();
  const [isPending, startTransition] = useTransition();
  const [shouldShowErrors, setShouldShowErrors] = useState(false);
  const [validatedSteps, setValidatedSteps] = useState<Set<number>>(new Set());

  const initialStep = Math.min(
    Math.max(parseInt(searchParams.get('step') || '1', 10), 1),
    totalSteps
  );
  const section = searchParams.get('section') || 'details';
  const [currentStep, setCurrentStep] = useState(initialStep);

  const form = useForm<z.infer<typeof AccountSettingsSchema>>({
    resolver: zodResolver(AccountSettingsSchema),
    defaultValues: {
      name: user.name ?? '',
      email: user.email ?? '',
      phone: user.phone ?? '',
      companyAddress: user.companyAddress ?? '',
      zipCode: user.zipCode ?? '',
      city: user.city ?? '',
      country: user.country ?? '',
      field: user.field ?? '',
      companyName: user.companyName ?? '',
      taxId: user.taxId ?? '',
      registrationNumber: user.registrationNumber ?? '',
      invoiceNumberPrefix: user.invoiceNumberPrefix ?? '',
      bankName: user.bankName ?? '',
      accountNumber: user.accountNumber ?? '',
      bic: user.bic ?? '',
      currency: user.currency?.toUpperCase() ?? '',
      logo: user.logo ?? '',
      enableExpenses: user.enableExpenses ?? false,
    },
    mode: 'onChange',
  });

  // Stable map identity
  const stepFieldMap = useMemo<
    Record<number, (keyof z.infer<typeof AccountSettingsSchema>)[]>
  >(
    () => ({
      1: ['name', 'email', 'phone', 'currency'],
      2: ['companyAddress', 'zipCode', 'city', 'country'],
      3: [
        'field',
        'companyName',
        'taxId',
        'registrationNumber',
        'invoiceNumberPrefix',
        'logo',
      ],
      4: ['bankName', 'accountNumber', 'bic'],
      5: ['enableExpenses'],
    }),
    []
  );

  const currencyOptions = useMemo(
    () => [
      { label: 'USD ($)', value: 'USD' },
      { label: 'EUR (€)', value: 'EUR' },
      { label: 'RSD (дин)', value: 'RSD' },
      { label: 'GBP (£)', value: 'GBP' },
    ],
    []
  );

  const progress = (currentStep / totalSteps) * 100;

  const getCurrentStepFields = useCallback(
    () => stepFieldMap[currentStep] || [],
    [stepFieldMap, currentStep]
  );

  const getCurrentStepValues = useCallback(() => {
    const currentFields = getCurrentStepFields();
    const allValues = form.getValues();

    return currentFields.reduce(
      (stepValues, field) => {
        return {
          ...stepValues,
          [field]: allValues[field],
        };
      },
      {} as Partial<
        Record<
          keyof z.infer<typeof AccountSettingsSchema>,
          string | boolean | undefined
        >
      >
    );
  }, [form, getCurrentStepFields]);

  const shouldShowCurrentStepErrors = useCallback(
    () => shouldShowErrors || validatedSteps.has(currentStep),
    [shouldShowErrors, validatedSteps, currentStep]
  );

  // Sync URL (no React state set here)
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      const params = new URLSearchParams();
      params.set('section', section);
      if (section === 'details') {
        params.set('step', currentStep.toString());
      }
      router.replace(`${window.location.pathname}?${params.toString()}`, {
        scroll: false,
      });
    }, 50);

    return () => clearTimeout(timeoutId);
  }, [currentStep, section, router]);

  // Trigger validation feedback for fields visible on this step when needed
  useEffect(() => {
    if (!shouldShowCurrentStepErrors()) return;

    const currentFields = getCurrentStepFields();
    // Trigger each field; react-hook-form handles internal state
    currentFields.forEach((field) => {
      void form.trigger(field);
    });
  }, [form, getCurrentStepFields, shouldShowCurrentStepErrors]);

  const validateCurrentStep = async (): Promise<boolean> => {
    const fieldsToValidate = stepFieldMap[currentStep] || [];
    const result = await form.trigger(fieldsToValidate);
    return result;
  };

  const { update: updateSession } = useSession();

  const onSubmit = async (values: z.infer<typeof AccountSettingsSchema>) => {
    setShouldShowErrors(true);
    setError(undefined);
    setSuccess(undefined);

    // Validate all fields before submitting
    const allFields = Object.values(stepFieldMap).flat();
    const isValid = await form.trigger(allFields as any);

    if (!isValid) {
      setError('Please fill in all required fields');
      return;
    }

    startTransition(async () => {
      try {
        const result = await accountSettings(values);

        if (result.success) {
          setSuccess(result.success);

          await updateSession?.();

          setTimeout(() => {
            router.push(redirectOnSuccess);
          }, 500);
        } else {
          setError(result.error);
        }
      } catch (e) {
        console.error(e);
        setError('Something went wrong!');
      }
    });
  };

  const nextStep = async () => {
    setShouldShowErrors(true);
    setValidatedSteps((prev) => new Set(prev).add(currentStep));
    const isValid = await validateCurrentStep();
    if (isValid && currentStep < totalSteps) {
      setCurrentStep((prev) => prev + 1);
      // reset transient UI flags on successful transition
      setError(undefined);
      setShouldShowErrors(false);
    }
  };

  const prevStep = () => {
    if (currentStep > 1) {
      setCurrentStep((prev) => prev - 1);
      setError(undefined);
      setShouldShowErrors(false);
    }
  };

  const goToStep = async (step: number) => {
    if (step === currentStep) return;

    if (step > currentStep) {
      setShouldShowErrors(true);
      setValidatedSteps((prev) => new Set(prev).add(currentStep));
      const isValid = await validateCurrentStep();
      if (!isValid) return;
    }

    setCurrentStep(step);
    setError(undefined);
    setShouldShowErrors(false);
  };

  const resetCurrentStepFields = () => {
    const currentFields = getCurrentStepFields();
    currentFields.forEach((field) => {
      const defaultValue = user[field as keyof typeof user] ?? '';
      form.setValue(field as never, defaultValue as never);
    });
  };

  const triggerCurrentStepValidation = async () => {
    setShouldShowErrors(true);
    return await validateCurrentStep();
  };

  const isStepCompleted = (step: number) => {
    const fields = stepFieldMap[step] || [];
    const values = form.getValues();

    return fields.every((field) => {
      const value = values[field];
      return value !== undefined && value !== null && value !== '';
    });
  };

  return {
    form,
    error,
    success,
    isPending,
    shouldShowErrors: shouldShowCurrentStepErrors(),

    currentStep,
    totalSteps,
    progress,

    nextStep,
    prevStep,
    goToStep,

    onSubmit,
    validateCurrentStep,
    triggerCurrentStepValidation,

    stepFieldMap,
    currencyOptions,
    getCurrentStepFields,
    getCurrentStepValues,
    resetCurrentStepFields,
    isStepCompleted,

    t,
    common,

    user,
  };
};
